import { type FC, type MouseEventHandler, useCallback } from "react";

import { Popover } from "@snapchat/snap-design-system";
import { CalendarIcon, CrossIcon } from "@snapchat/snap-design-system-icons";
import { produce } from "immer";

import type { ActionDisabledTooltipProps } from "../../../common/components/ActionDisabledTooltip";
import { ActionDisabledTooltip, DisabledButtonToolipFix } from "../../../common/components/ActionDisabledTooltip";
import { InfoTooltip, DisplayComponent } from "../../../common/components";
import { RangeModifier } from "../../../connections/models";
import { formatMediumDate } from "../../../common/formatters";
import { Flexbox } from "../../../core/components";

import { ScheduleDatePicker } from "./ScheduleDatePicker";
import {
    ScheduleDatePickerInputContainer,
    buttonStyle,
    datePickerContainer,
    datePickerLabel,
    popoverStyle,
} from "./ScheduleDateRange.styles";

interface ScheduleDateRangeProps {
    onChange: (range: RangeModifier) => void;
    range: RangeModifier;
    isDisabled?: boolean;
}

const nullDate = new Date(0);

export const ScheduleDateRange: FC<ScheduleDateRangeProps & ActionDisabledTooltipProps> = ({
    onChange,
    range,
    isDisabled,
    placement,
    hasPermission,
    hasMusic,
}) => {
    const isNullDates = !range.from || !range.to;
    const initialMonth = isNullDates ? new Date() : undefined;

    const showRangeValue: RangeModifier = produce(range, (draft) => {
        draft.from = draft.from ?? nullDate;
        draft.to = draft.to ?? nullDate;
    });

    const handleReset = useCallback<MouseEventHandler<HTMLDivElement>>(
        (event) => {
            event.preventDefault();
            event.stopPropagation();
            onChange(new RangeModifier());
        },
        [onChange],
    );

    return (
        <Flexbox className={datePickerContainer} flexDirection="column">
            <span className={datePickerLabel}>
                Starts &amp; Ends
                <InfoTooltip content="Scheduled dates" />
            </span>
            <ActionDisabledTooltip placement={placement} hasPermission={hasPermission} hasMusic={hasMusic}>
                <ScheduleDatePickerInputContainer>
                    <Popover
                        className={popoverStyle}
                        content={
                            <ScheduleDatePicker
                                range={showRangeValue}
                                onChange={onChange}
                                initialMonth={initialMonth}
                            />
                        }
                        trigger="click"
                    >
                        <DisabledButtonToolipFix isDisabled={isDisabled} className={buttonStyle}>
                            <span>
                                {isNullDates
                                    ? "Always"
                                    : `${formatMediumDate(showRangeValue.from as Date)} - ${formatMediumDate(
                                          showRangeValue.to as Date,
                                      )}`}
                            </span>
                            <DisplayComponent show={isNullDates}>
                                <CalendarIcon />
                            </DisplayComponent>
                            <DisplayComponent show={!isNullDates} onClick={handleReset}>
                                <CrossIcon />
                            </DisplayComponent>
                        </DisabledButtonToolipFix>
                    </Popover>
                </ScheduleDatePickerInputContainer>
            </ActionDisabledTooltip>
        </Flexbox>
    );
};
